import { useState, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';

import { getVulsPeriodChart } from 'api/analitics/VulCharts/getVulsPeriodChart';
import { VulsPeriodChartResponse } from 'api/analitics/VulCharts/types';
import { Period } from 'components/AnalyticsPage/Periods';

interface UseGetVulsLineChartStateProps {
  slug: string;
}

export const useGetVulsLineChartState = ({ slug }: UseGetVulsLineChartStateProps) => {
  const [period, setPeriod] = useState<Period>(Period.YEAR);
  const [startDate, setStartDate] = useState<string>(() => dayjs().subtract(1, 'year').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(() => dayjs().format('YYYY-MM-DD'));
  const [vulType, setVulType] = useState('all_vuls');

  const {
    data: chartData,
    isLoading,
    refetch,
  } = useQuery<VulsPeriodChartResponse>(
    ['vulsPeriodChart', slug, startDate, endDate],
    () => getVulsPeriodChart({ slug, startDate, endDate }),
    {
      enabled: !!slug && !!startDate && !!endDate,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  );

  const handlePeriodChange = useCallback(
    (newPeriod: Period) => {
      setPeriod(newPeriod);

      if (newPeriod !== Period.CUSTOM) {
        const end = dayjs().format('YYYY-MM-DD');
        let start = dayjs();

        if (newPeriod === Period.MONTH) {
          start = start.subtract(1, 'month');
        } else if (newPeriod === Period.THREE_MONTH) {
          start = start.subtract(3, 'month');
        } else if (newPeriod === Period.YEAR) {
          start = start.subtract(1, 'year');
        }

        setStartDate(start.format('YYYY-MM-DD'));
        setEndDate(end);
      }
    },
    [setPeriod, setStartDate, setEndDate],
  );

  useEffect(() => {
    refetch();
  }, [startDate, endDate, refetch]);

  return {
    period,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    vulType,
    setVulType,
    chartData,
    isLoading,
    handlePeriodChange,
  };
};
